import * as React from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogTitle,
} from "@repo/ui/components/alert-dialog";

export type ConfirmDialogOptions = {
  title?: React.ReactNode;
  description?: React.ReactNode | string;
  confirmText?: React.ReactNode;
  cancelText?: React.ReactNode;
  confirmButtonVariant?: React.ComponentProps<typeof AlertDialogAction>["variant"];
  cancelButtonVariant?: React.ComponentProps<typeof AlertDialogCancel>["variant"];
  size?: React.ComponentProps<typeof AlertDialogContent>["size"];
  media?: React.ReactNode;
};

export type ConfirmDialogInput = string | ConfirmDialogOptions;
type ConfirmDialogFn = (input?: ConfirmDialogInput) => Promise<boolean>;

type ResolvedConfirmDialogOptions = Required<
  Pick<
    ConfirmDialogOptions,
    "title" | "confirmText" | "cancelText" | "confirmButtonVariant" | "cancelButtonVariant" | "size"
  >
> &
  Pick<ConfirmDialogOptions, "description" | "media">;

type PendingRequest = {
  options: ResolvedConfirmDialogOptions;
  resolve: (value: boolean) => void;
};

const DEFAULT_OPTIONS: ResolvedConfirmDialogOptions = {
  title: "Are you sure?",
  description: undefined,
  confirmText: "Continue",
  cancelText: "Cancel",
  confirmButtonVariant: "default",
  cancelButtonVariant: "outline",
  size: "default",
  media: undefined,
};

const ConfirmationDialogContext = React.createContext<ConfirmDialogFn | undefined>(undefined);

function renderDescription(description: React.ReactNode | string | undefined) {
  if (description === undefined || description === null || description === false) {
    return null;
  }

  const isChildren = typeof description !== "string" && React.isValidElement(description);

  return (
    <AlertDialogDescription render={isChildren ? <div /> : undefined}>
      {description}
    </AlertDialogDescription>
  );
}

function normalizeOptions(input?: ConfirmDialogInput): ResolvedConfirmDialogOptions {
  if (typeof input === "string") {
    return {
      ...DEFAULT_OPTIONS,
      description: input,
    };
  }

  if (!input) {
    return { ...DEFAULT_OPTIONS };
  }

  return {
    ...DEFAULT_OPTIONS,
    ...input,
  };
}

export function ConfirmationDialogProvider({ children }: { children: React.ReactNode }) {
  const [currentRequest, setCurrentRequest] = React.useState<PendingRequest | null>(null);
  const queueRef = React.useRef<PendingRequest[]>([]);
  const currentRequestRef = React.useRef<PendingRequest | null>(null);

  React.useEffect(() => {
    currentRequestRef.current = currentRequest;
  }, [currentRequest]);

  const openNext = React.useCallback(() => {
    setCurrentRequest((prev) => prev ?? queueRef.current.shift() ?? null);
  }, []);

  React.useEffect(() => {
    if (!currentRequest) {
      openNext();
    }
  }, [currentRequest, openNext]);

  React.useEffect(() => {
    return () => {
      if (currentRequestRef.current) {
        currentRequestRef.current.resolve(false);
        currentRequestRef.current = null;
      }

      for (const queuedRequest of queueRef.current) {
        queuedRequest.resolve(false);
      }

      queueRef.current = [];
    };
  }, []);

  const confirm = React.useCallback(
    (input?: ConfirmDialogInput) =>
      new Promise<boolean>((resolve) => {
        queueRef.current.push({
          options: normalizeOptions(input),
          resolve,
        });

        openNext();
      }),
    [openNext],
  );

  const settle = React.useCallback((value: boolean) => {
    setCurrentRequest((prev) => {
      if (!prev) return prev;
      prev.resolve(value);
      return null;
    });
  }, []);

  return (
    <ConfirmationDialogContext.Provider value={confirm}>
      {children}
      <AlertDialog
        open={Boolean(currentRequest)}
        onOpenChange={(open) => {
          if (!open) {
            settle(false);
          }
        }}
      >
        <AlertDialogContent size={currentRequest?.options.size}>
          <AlertDialogHeader>
            {currentRequest?.options.media ? (
              <AlertDialogMedia>{currentRequest.options.media}</AlertDialogMedia>
            ) : null}
            <AlertDialogTitle>{currentRequest?.options.title}</AlertDialogTitle>
            {renderDescription(currentRequest?.options.description)}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              variant={currentRequest?.options.cancelButtonVariant}
              onClick={() => settle(false)}
            >
              {currentRequest?.options.cancelText}
            </AlertDialogCancel>
            <AlertDialogAction
              variant={currentRequest?.options.confirmButtonVariant}
              onClick={() => settle(true)}
            >
              {currentRequest?.options.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmationDialogContext.Provider>
  );
}

export function useConfirmationDialog() {
  const context = React.useContext(ConfirmationDialogContext);

  if (context === undefined) {
    throw new Error("useConfirmationDialog must be used within a ConfirmationDialogProvider");
  }

  return context;
}
